// One project: its board (drag a card to another column or place; a finger opens the task and picks a status), the
// same tasks as a list, and what has happened. A task opens in a panel beside it (?task=<id>), so a link to a task is
// a link to its project with the task open.
import { useMemo, useState, type DragEvent, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { formatShort, keys, type TeamSummary } from "../../lib/chat";
import {
  APPROVAL_LABEL, APPROVAL_STYLE, COLUMNS, PROJECT_STATUSES, PROJECT_STATUS_LABEL, PRIORITY_LABEL, PRIORITY_STYLE, STATUS_DOT, STATUS_LABEL,
  activityText, dayLabel, isOverdue, projectKeys, type Activity, type Milestone, type Project, type Task, type TaskStatus,
} from "../../lib/projects";
import { Alert, Avatar, Badge, Button, Card, Dialog, Field, Select, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";
import { useProjectEvents } from "./events";
import { TaskPanel } from "./task";
import { ProjectMeetings } from "./meetings";
import { ProjectOverview } from "./overview";
import { ProjectFiles } from "./files";

type View = "board" | "list" | "overview" | "meetings" | "files" | "activity";
type OrgMember = { id: string; name: string; email: string; role: "owner" | "admin" | "member" | "guest"; status: "active" | "suspended" };

export function ProjectPage() {
  const { projectId = "" } = useParams();
  const { org, me } = useOrg();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view: View = (["list", "overview", "meetings", "files", "activity"] as const).find((v) => v === params.get("view")) ?? "board";
  const [milestone, setMilestone] = useState("");
  const openTask = params.get("task");
  const [onlyMine, setOnlyMine] = useState(false);
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState<"members" | "settings" | "request" | null>(null);
  const [error, setError] = useState<string | null>(null);
  useProjectEvents(org.slug);

  const project = useQuery({ queryKey: projectKeys.one(org.slug, projectId), queryFn: () => api<{ project: Project }>(`/orgs/${org.slug}/projects/${projectId}`).then((r) => r.project), retry: false });
  const tasks = useQuery({ queryKey: projectKeys.tasks(org.slug, projectId), queryFn: () => api<{ tasks: Task[] }>(`/orgs/${org.slug}/projects/${projectId}/tasks`).then((r) => r.tasks), enabled: project.isSuccess });
  const milestones = useQuery({
    queryKey: projectKeys.milestones(org.slug, projectId), enabled: project.isSuccess,
    queryFn: () => api<{ milestones: Milestone[] }>(`/orgs/${org.slug}/projects/${projectId}/milestones`).then((r) => r.milestones),
  });
  const p = project.data;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key); else next.set(key, value);
    if (key === "view") next.delete("add");   // a tab of its own, not the one opened to add a milestone
    setParams(next, { replace: key !== "task" });
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (tasks.data ?? []).filter((t) => (!onlyMine || t.assignee?.id === me.user.id) && (!milestone || t.milestoneId === milestone)
      && (!q || t.title.toLowerCase().includes(q) || t.ref.toLowerCase().includes(q)));
  }, [tasks.data, onlyMine, filter, milestone, me.user.id]);

  if (project.isLoading) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  if (project.error || !p) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-lg font-semibold">This project isn't available</p>
        <p className="mt-1 text-sm text-slate-500">It may have been archived, made private, or you were taken off it.</p>
        <Link to={`/o/${org.slug}/projects`}><Button variant="secondary" className="mt-4">All projects</Button></Link>
      </div>
    );
  }

  async function join() {
    setError(null);
    try { await api(`/orgs/${org.slug}/projects/${p!.id}/join`, { method: "POST" }); await qc.invalidateQueries({ queryKey: projectKeys.one(org.slug, p!.id) }); } catch (err) { setError(errorText(err)); }
  }

  return (
    <div className="mx-auto flex h-full max-w-[100rem] flex-col">
      <Link to={`/o/${org.slug}/projects`} className="mb-2 inline-flex w-fit items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <Icon name="chevron" className="h-4 w-4 rotate-90" />Projects
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
            <span className="min-w-0 break-words">{p.name}</span>
            {p.visibility === "private" && <Icon name="lock" className="h-5 w-5 text-slate-400" />}
            <Badge tone={p.status === "active" ? "brand" : p.status === "on_hold" ? "amber" : "slate"}>{PROJECT_STATUS_LABEL[p.status]}</Badge>
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            <span className="font-mono">{p.key}</span>
            {p.team && (p.team.channelId
              ? <Link to={`/o/${org.slug}/chat/${p.team.channelId}`} className="inline-flex items-center gap-1 text-brand-600 hover:underline"><Icon name="hash" className="h-3.5 w-3.5" />{p.team.name}</Link>
              : <span>{p.team.name}</span>)}
            {(p.startDate || p.dueDate) && <span>{p.startDate ? dayLabel(p.startDate) : "…"} → {p.dueDate ? dayLabel(p.dueDate) : "…"}</span>}
            <span>{p.tasks.done} of {p.tasks.total} done</span>
            {p.tasks.overdue > 0 && <span className="font-medium text-red-600">{p.tasks.overdue} overdue</span>}
          </p>
          {p.description && <p className="mt-2 max-w-3xl whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{p.description}</p>}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* The project's people - its team, and its clients - say so in words: a row of faces alone was missed. */}
          <button type="button" onClick={() => setDialog("members")} aria-label={`People in this project: ${p.members.length}`}
            className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            <span className="flex -space-x-2">
              {p.members.slice(0, 4).map((m) => <span key={m.id} className="rounded-full ring-2 ring-white dark:ring-slate-950"><Avatar name={m.name} userId={m.id} size="sm" /></span>)}
              {p.members.length > 4 && <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-200 text-xs font-medium ring-2 ring-white dark:bg-slate-700 dark:ring-slate-950">+{p.members.length - 4}</span>}
            </span>
            <span>People{p.clients.length > 0 && p.myRole !== "client" ? ` · ${p.clients.length} client${p.clients.length === 1 ? "" : "s"}` : ""}</span>
          </button>
          {p.channels.map((c) => (
            <Link key={c.id} to={`/o/${org.slug}/chat/${c.id}`}>
              <Button size="sm" variant="secondary"><Icon name="chat" className="h-4 w-4" />{c.audience === "client" ? (p.myRole === "client" ? "Chat with the team" : "Chat with the client") : "Project chat"}</Button>
            </Link>
          ))}
          {!p.joined && p.myRole !== "admin" && <Button size="sm" variant="secondary" onClick={join}>Join</Button>}
          {p.can.request && <Button size="sm" onClick={() => setDialog("request")}><Icon name="plus" className="h-4 w-4" />Ask for something</Button>}
          {p.can.update && <Button size="sm" variant="ghost" onClick={() => setDialog("settings")}><Icon name="settings" className="h-4 w-4" />Settings</Button>}
        </div>
      </div>
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}
      {p.can.seeInternal && p.clients.length > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-100 dark:ring-amber-800">
          <Icon name="users" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{p.clients.map((c) => c.name).join(", ")} {p.clients.length === 1 ? "is this project's client and sees" : "are this project's clients and see"} this board, except tasks marked <strong>Team only</strong> and team notes.</span>
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800">
        <div className="-mx-1 flex max-w-full gap-1 overflow-x-auto px-1" role="tablist">
          {([["board", "Board"], ["list", "List"], ["overview", "Overview"], ["meetings", "Meetings"], ["files", "Files"], ["activity", "Activity"]] as const).map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={view === value} onClick={() => setParam("view", value === "board" ? null : value)}
              className={cx("-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium", view === value ? "border-brand-600 text-brand-700 dark:text-brand-100" : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}>
              {label}
            </button>
          ))}
        </div>
        {(view === "board" || view === "list") && (
          <div className="mb-2 flex w-full items-center gap-3 sm:w-auto">
            <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter tasks" aria-label="Filter tasks"
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm sm:w-48 sm:flex-none dark:border-slate-700 dark:bg-slate-900" />
            {((milestones.data?.length ?? 0) > 0 || p.can.update) && (
              <select
                value={milestone} aria-label="Milestone"
                onChange={(e) => {
                  if (e.target.value === "__add") {
                    const next = new URLSearchParams(params);
                    next.set("view", "overview"); next.set("add", "milestone");
                    setParams(next, { replace: true });
                    return;
                  }
                  setMilestone(e.target.value);
                }}
                className="min-w-0 max-w-[11rem] rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">All milestones</option>
                {(milestones.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                {p.can.update && <option value="__add">+ Add a milestone…</option>}
              </select>
            )}
            {p.myRole !== "client" && <label className="flex shrink-0 items-center gap-2 text-sm text-slate-600 dark:text-slate-400"><input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} className="h-4 w-4 rounded" />Mine</label>}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 pt-4">
        {tasks.isLoading && (view === "board" || view === "list") && <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>}
        {tasks.data && view === "board" && <Board project={p} tasks={visible} all={tasks.data} onOpen={(id) => setParam("task", id)} />}
        {tasks.data && view === "list" && <TaskList project={p} tasks={visible} onOpen={(id) => setParam("task", id)} />}
        {view === "overview" && <ProjectOverview project={p} onOpen={(id) => setParam("task", id)} onActivity={() => setParam("view", "activity")} addMilestone={params.get("add") === "milestone"} />}
        {view === "meetings" && <ProjectMeetings project={p} />}
        {view === "files" && <ProjectFiles project={p} onOpen={(id) => setParam("task", id)} />}
        {view === "activity" && <ActivityFeed projectId={p.id} onOpen={(id) => setParam("task", id)} />}
      </div>

      {openTask && <TaskPanel project={p} taskId={openTask} milestones={milestones.data ?? []} onClose={() => setParam("task", null)} />}
      {dialog === "members" && <MembersDialog project={p} onClose={() => setDialog(null)} />}
      {dialog === "settings" && <SettingsDialog project={p} onClose={() => setDialog(null)} />}
      {dialog === "request" && <RequestDialog project={p} onClose={() => setDialog(null)} onCreated={(id) => { setDialog(null); setParam("task", id); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- the board

function Board({ project, tasks, all, onOpen }: { project: Project; tasks: Task[]; all: Task[]; onOpen: (id: string) => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [dragging, setDragging] = useState<string | null>(null);
  const [hint, setHint] = useState<{ status: TaskStatus; beforeId: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canMove = project.can.createTasks;
  const key = projectKeys.tasks(org.slug, project.id);

  // Where in a column the pointer is: before the first card whose middle is below it.
  function spotIn(e: DragEvent<HTMLElement>, status: TaskStatus) {
    const cards = [...e.currentTarget.querySelectorAll<HTMLElement>("[data-task-id]")].filter((c) => c.dataset.taskId !== dragging);
    const before = cards.find((c) => { const r = c.getBoundingClientRect(); return e.clientY < r.top + r.height / 2; });
    return { status, beforeId: before?.dataset.taskId ?? null };
  }

  async function drop(status: TaskStatus, beforeId: string | null) {
    const id = dragging;
    setDragging(null); setHint(null);
    if (!id) return;
    const column = all.filter((t) => t.status === status && t.id !== id).sort((a, b) => a.position - b.position);
    const at = beforeId ? column.findIndex((t) => t.id === beforeId) : column.length;
    const after = at > 0 ? column[at - 1]! : null;
    const next = at >= 0 && at < column.length ? column[at]! : null;
    if (!all.some((t) => t.id === id)) return;
    // Shown moved at once; the server's answer replaces the guess.
    const position = after && next ? (after.position + next.position) / 2 : after ? after.position + 1024 : next ? next.position - 1024 : 1024;
    qc.setQueryData<Task[]>(key, (old) => old?.map((t) => (t.id === id ? { ...t, status, position } : t)));
    setError(null);
    try {
      await api(`/orgs/${org.slug}/tasks/${id}/move`, { method: "POST", body: { status, afterId: after?.id ?? null } });
    } catch (err) { setError(errorText(err)); }
    await qc.invalidateQueries({ queryKey: key });
  }

  return (
    <div className="flex h-full flex-col">
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      <div className="-mx-4 flex min-h-0 flex-1 gap-3 overflow-x-auto px-4 pb-4 md:-mx-6 md:px-6" aria-label="Board">
        {COLUMNS.map((col) => {
          const cards = tasks.filter((t) => t.status === col.status).sort((a, b) => a.position - b.position || a.number - b.number);
          return (
            <section
              key={col.status} aria-label={col.label}
              className={cx("flex w-[17rem] shrink-0 flex-col rounded-2xl bg-slate-100/80 p-2 dark:bg-slate-900/60", hint?.status === col.status && "ring-2 ring-brand-300 dark:ring-brand-700")}
              onDragOver={(e) => { if (!dragging) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; const s = spotIn(e, col.status); if (s.status !== hint?.status || s.beforeId !== hint?.beforeId) setHint(s); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setHint((h) => (h?.status === col.status ? null : h)); }}
              onDrop={(e) => { e.preventDefault(); void drop(col.status, spotIn(e, col.status).beforeId); }}
            >
              <h2 className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold">
                <span className={cx("h-2.5 w-2.5 rounded-full", STATUS_DOT[col.status])} />{col.label}
                <span className="font-normal text-slate-500">{cards.length}</span>
              </h2>
              <ul className="flex min-h-12 flex-1 flex-col gap-2 overflow-y-auto p-0.5">
                {cards.map((t) => (
                  <li key={t.id} data-task-id={t.id}>
                    {hint?.status === col.status && hint.beforeId === t.id && <DropLine />}
                    <TaskCard task={t} draggable={canMove} dragging={dragging === t.id} onOpen={() => onOpen(t.id)}
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; setDragging(t.id); }}
                      onDragEnd={() => { setDragging(null); setHint(null); }} />
                  </li>
                ))}
                {hint?.status === col.status && hint.beforeId === null && <li><DropLine /></li>}
              </ul>
              {project.can.createTasks && <QuickAdd project={project} status={col.status} />}
            </section>
          );
        })}
      </div>
    </div>
  );
}

const DropLine = () => <div className="mb-2 h-1 rounded-full bg-brand-500" aria-hidden="true" />;

function TaskCard({ task: t, draggable, dragging, onOpen, onDragStart, onDragEnd }: {
  task: Task; draggable: boolean; dragging: boolean; onOpen: () => void;
  onDragStart: (e: DragEvent<HTMLButtonElement>) => void; onDragEnd: () => void;
}) {
  const overdue = isOverdue(t);
  return (
    <button
      type="button" onClick={onOpen} draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}
      className={cx("block w-full rounded-xl bg-white p-3 text-left shadow-sm ring-1 ring-slate-200 transition hover:ring-brand-300 dark:bg-slate-800 dark:ring-slate-700 dark:hover:ring-brand-700", dragging && "opacity-40", draggable && "cursor-grab active:cursor-grabbing")}
    >
      <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500">
        {t.ref}
        {t.internal && <span className="inline-flex items-center gap-0.5 font-sans text-amber-700 dark:text-amber-300"><Icon name="lock" className="h-3 w-3" />Team only</span>}
        {t.clientRequest && <span className="font-sans text-brand-700 dark:text-brand-100">From client</span>}
      </span>
      <span className={cx("mt-0.5 block break-words text-sm font-medium", t.status === "done" && "text-slate-500 line-through")}>{t.title}</span>
      {t.approval && <span className={cx("mt-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", APPROVAL_STYLE[t.approval.status])}>{APPROVAL_LABEL[t.approval.status]}</span>}
      <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
        {t.priority !== "none" && <span className={cx("font-medium", PRIORITY_STYLE[t.priority])}>{PRIORITY_LABEL[t.priority]}</span>}
        {t.dueDate && <span className={cx(overdue && "font-medium text-red-600")}>{overdue ? "Overdue · " : ""}{dayLabel(t.dueDate)}</span>}
        {t.commentCount > 0 && <span className="inline-flex items-center gap-0.5"><Icon name="chat" className="h-3.5 w-3.5" />{t.commentCount}</span>}
        {t.fromMeeting && <span title="Made from a meeting's transcript"><Icon name="video" className="h-3.5 w-3.5" /></span>}
        {t.hasDescription && <Icon name="file" className="h-3.5 w-3.5" />}
        <span className="ml-auto">{t.assignee ? <Avatar name={t.assignee.name} userId={t.assignee.id} size="sm" /> : null}</span>
      </span>
    </button>
  );
}

// "Add a task" at the foot of a column: type a title, press Enter, carry on with the next.
function QuickAdd({ project, status }: { project: Project; status: TaskStatus }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function add(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setOpen(false); return; }
    setBusy(true); setError(null);
    try {
      await api(`/orgs/${org.slug}/projects/${project.id}/tasks`, { method: "POST", body: { title: title.trim(), status } });
      setTitle("");
      await qc.invalidateQueries({ queryKey: projectKeys.tasks(org.slug, project.id) });
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-1 flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-sm text-slate-500 hover:bg-white hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200">
        <Icon name="plus" className="h-4 w-4" />Add a task
      </button>
    );
  }
  return (
    <form onSubmit={add} className="mt-1 space-y-2 p-0.5">
      <textarea
        autoFocus rows={2} value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} aria-label={`New task in ${STATUS_LABEL[status]}`} placeholder="What needs doing?"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void add(e); } if (e.key === "Escape") { setOpen(false); setTitle(""); } }}
        className="block w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-800"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" type="submit" loading={busy}>Add</Button>
        <Button size="sm" type="button" variant="ghost" onClick={() => { setOpen(false); setTitle(""); }}>Cancel</Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- the list

function TaskList({ project, tasks, onOpen }: { project: Project; tasks: Task[]; onOpen: (id: string) => void }) {
  if (!tasks.length) {
    return <Card className="p-10 text-center text-sm text-slate-500">{project.tasks.total ? "No tasks match." : "No tasks yet. Add the first one on the board."}</Card>;
  }
  return (
    <div className="space-y-5 pb-6">
      {COLUMNS.map((col) => {
        const rows = tasks.filter((t) => t.status === col.status).sort((a, b) => a.position - b.position);
        if (!rows.length) return null;
        return (
          <section key={col.status}>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><span className={cx("h-2.5 w-2.5 rounded-full", STATUS_DOT[col.status])} />{col.label}<span className="font-normal text-slate-500">{rows.length}</span></h2>
            <Card className="p-0">
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((t) => (
                  <li key={t.id}>
                    <button type="button" onClick={() => onOpen(t.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60">
                      <span className="font-mono text-xs text-slate-500">{t.ref}</span>
                      <span className={cx("min-w-0 flex-1 break-words text-sm font-medium", t.status === "done" && "text-slate-500 line-through")}>{t.title}</span>
                      {t.internal && <span className="text-xs text-amber-700 dark:text-amber-300">Team only</span>}
                      {t.approval && <span className={cx("rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", APPROVAL_STYLE[t.approval.status])}>{APPROVAL_LABEL[t.approval.status]}</span>}
                      <span className="flex items-center gap-3 text-xs text-slate-500">
                        {t.priority !== "none" && <span className={PRIORITY_STYLE[t.priority]}>{PRIORITY_LABEL[t.priority]}</span>}
                        {t.dueDate && <span className={cx(isOverdue(t) && "font-medium text-red-600")}>{dayLabel(t.dueDate)}</span>}
                        {t.assignee ? <span className="flex items-center gap-1.5"><Avatar name={t.assignee.name} userId={t.assignee.id} size="sm" /><span className="hidden sm:inline">{t.assignee.name}</span></span> : <span>Unassigned</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- activity

function ActivityFeed({ projectId, onOpen }: { projectId: string; onOpen: (id: string) => void }) {
  const { org } = useOrg();
  const feed = useQuery({ queryKey: projectKeys.activity(org.slug, projectId), queryFn: () => api<{ activity: Activity[] }>(`/orgs/${org.slug}/projects/${projectId}/activity`).then((r) => r.activity) });
  if (feed.isLoading) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  return (
    <Card className="p-0">
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {(feed.data ?? []).map((a) => (
          <li key={a.id} className="flex items-start gap-3 px-4 py-3">
            <Avatar name={a.actor?.name ?? "?"} userId={a.actor?.id} size="sm" />
            <p className="min-w-0 flex-1 text-sm">
              <span className="font-medium">{a.actor?.name ?? "Someone"}</span>{" "}
              {a.taskId && a.kind !== "task.deleted"
                ? <button type="button" onClick={() => onOpen(a.taskId!)} className="text-left hover:underline">{activityText(a, true)}</button>
                : activityText(a, true)}
              {a.kind === "task.commented" && typeof a.data.excerpt === "string" && <span className="mt-0.5 block truncate text-slate-500">"{a.data.excerpt}"</span>}
            </p>
            <span className="shrink-0 text-xs text-slate-400">{formatShort(a.createdAt)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------- people and settings

function MembersDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const { org, me, role } = useOrg();
  const atLeastAdmin = role === "owner" || role === "admin";
  const qc = useQueryClient();
  const everyone = useQuery({ queryKey: keys.members(org.slug), queryFn: () => api<{ members: OrgMember[] }>(`/orgs/${org.slug}/members`) });
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inIt = new Set(project.members.map((m) => m.id));
  const candidates = (everyone.data?.members ?? []).filter((m) => m.status === "active" && m.role !== "guest" && !inIt.has(m.id)).sort((a, b) => a.name.localeCompare(b.name));
  const refresh = () => qc.invalidateQueries({ queryKey: projectKeys.one(org.slug, project.id) });
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try { await fn(); await refresh(); } catch (err) { setError(errorText(err)); }
  }
  return (
    <Dialog open onClose={onClose} title="People in this project">
      {project.myRole === "client" && <p className="mb-3 text-sm text-slate-500">The team at {org.name} working on it.</p>}
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      {project.myRole !== "client" && <p className="mb-3 text-sm text-slate-500">
        {project.visibility === "public" ? `Everyone in ${org.name} can see this project and work on it; these are the people in it.` : "Only these people (and the organization's admins) can see this project."}
        {" "}Leads change the project and who is in it.
      </p>}
      <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
        {project.members.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-3 py-2.5">
            <Avatar name={m.name} userId={m.id} size="sm" />
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{m.name}{m.id === me.user.id ? " (you)" : ""}</span>{m.email && <span className="block truncate text-xs text-slate-500">{m.email}</span>}</span>
            {project.can.members ? (
              <select value={m.role} aria-label={`${m.name}'s role`} onChange={(e) => run(() => api(`/orgs/${org.slug}/projects/${project.id}/members/${m.id}`, { method: "PATCH", body: { role: e.target.value } }))}
                className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900">
                <option value="lead">Lead</option><option value="member">Member</option>
              </select>
            ) : <Badge tone={m.role === "lead" ? "brand" : "slate"}>{m.role === "lead" ? "Lead" : "Member"}</Badge>}
            {(project.can.members || m.id === me.user.id) && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                if (m.id === me.user.id && !confirm("Leave this project?")) return;
                void run(() => api(`/orgs/${org.slug}/projects/${project.id}/members/${m.id}`, { method: "DELETE" }));
              }}>{m.id === me.user.id ? "Leave" : "Remove"}</Button>
            )}
          </li>
        ))}
      </ul>
      {(project.clients.length > 0 || project.pendingClients.length > 0 || project.can.inviteClients) && <ClientsSection project={project} run={run} />}
      {project.can.members && !project.clientsEnabled && atLeastAdmin && (
        <p className="mt-4 text-sm text-slate-500">Working for a client? Turn on <strong>We work with clients</strong> in the organization's settings to invite them to this project.</p>
      )}
      {project.can.members && candidates.length > 0 && (
        <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (adding) void run(() => api(`/orgs/${org.slug}/projects/${project.id}/members`, { method: "POST", body: { userIds: [adding] } })).then(() => setAdding("")); }}>
          <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Person to add" className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
            <option value="">Add someone…</option>
            {candidates.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <Button type="submit" disabled={!adding}>Add</Button>
        </form>
      )}
    </Dialog>
  );
}

function SettingsDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: keys.teams(org.slug), queryFn: () => api<{ teams: TeamSummary[] }>(`/orgs/${org.slug}/teams`) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const text = (k: string) => String(d.get(k) ?? "").trim();
    setBusy(true); setError(null); setFields({});
    try {
      await api(`/orgs/${org.slug}/projects/${project.id}`, {
        method: "PATCH",
        body: {
          name: text("name"), description: text("description"), status: text("status"), visibility: text("visibility"),
          teamId: text("teamId") || null, startDate: text("startDate") || null, dueDate: text("dueDate") || null,
        },
      });
      await qc.invalidateQueries({ queryKey: projectKeys.one(org.slug, project.id) });
      onClose();
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError) setFields(err.fields);
      setBusy(false);
    }
  }
  async function archive() {
    if (!confirm(`Archive "${project.name}"? It disappears from everyone's lists; its tasks are kept, and a lead or admin can bring it back.`)) return;
    try {
      await api(`/orgs/${org.slug}/projects/${project.id}/archive`, { method: "POST" });
      await qc.invalidateQueries({ queryKey: projectKeys.all(org.slug) });
      window.location.assign(`/o/${org.slug}/projects`);
    } catch (err) { setError(errorText(err)); }
  }
  const teamOptions = (teams.data?.teams ?? []).filter((t) => t.myRole || t.id === project.team?.id);
  return (
    <Dialog open onClose={onClose} title="Project settings">
      <form onSubmit={save} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <Field label="Name" name="name" defaultValue={project.name} required maxLength={120} error={fields.name} />
        <div>
          <label htmlFor="settings-description" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
          <textarea id="settings-description" name="description" rows={3} maxLength={5000} defaultValue={project.description} className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Status" name="status" defaultValue={project.status}>{PROJECT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select>
          <Select label="Who can see it" name="visibility" defaultValue={project.visibility}>
            <option value="public">Everyone in {org.name}</option><option value="private">Only people in it</option>
          </Select>
        </div>
        <Select label="Discussed in" name="teamId" defaultValue={project.team?.id ?? ""}>
          <option value="">No team</option>
          {teamOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Starts" name="startDate" type="date" defaultValue={project.startDate ?? ""} error={fields.startDate} />
          <Field label="Due" name="dueDate" type="date" defaultValue={project.dueDate ?? ""} error={fields.dueDate} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {project.can.archive ? <Button type="button" variant="ghost" className="text-red-600" onClick={archive}>Archive project</Button> : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={busy}>Save</Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

// The project's clients: who they are, invitations not yet accepted, and inviting one by email (someone already in the
// organization as a guest is added straight away; anyone else gets an invitation to this project).
function ClientsSection({ project, run }: { project: Project; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { org, me } = useOrg();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const base = `/orgs/${org.slug}/projects/${project.id}`;
  return (
    <section className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
      <h3 className="text-sm font-semibold">Clients</h3>
      {project.myRole !== "client" && <p className="mt-0.5 text-xs text-slate-500">They see this project only: the board and comments, but not tasks marked team only or team notes.</p>}
      <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
        {project.clients.map((c) => (
          <li key={c.id} className="flex items-center gap-3 py-2.5">
            <Avatar name={c.name} userId={c.id} size="sm" />
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{c.name}{c.id === me.user.id ? " (you)" : ""}</span>{c.email && <span className="block truncate text-xs text-slate-500">{c.email}</span>}</span>
            <Badge tone="amber">Client</Badge>
            {project.can.members && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (confirm(`Take ${c.name} off this project? They lose access to it.`)) void run(() => api(`${base}/members/${c.id}`, { method: "DELETE" })); }}>Remove</Button>}
          </li>
        ))}
        {project.pendingClients.map((i) => (
          <li key={i.id} className="flex items-center gap-3 py-2.5">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800"><Icon name="send" className="h-4 w-4" /></span>
            <span className="min-w-0 flex-1"><span className="block truncate text-sm">{i.email}</span><span className="block text-xs text-slate-500">Invited, hasn't joined yet</span></span>
            <Button size="sm" variant="ghost" onClick={() => void run(() => api(`${base}/client-invitations/${i.id}`, { method: "DELETE" }))}>Withdraw</Button>
          </li>
        ))}
      </ul>
      {project.can.inviteClients && (
        <form className="mt-3 flex gap-2" onSubmit={(e) => {
          e.preventDefault();
          const to = email.trim();
          if (!to) return;
          void run(() => api<{ added: boolean }>(`${base}/clients`, { method: "POST", body: { email: to } }).then((r) => {
            setSent(r.added ? `${to} is in the project now, and has been emailed.` : `Invitation sent to ${to}.`);
            setEmail("");
          }));
        }}>
          <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setSent(null); }} placeholder="client@company.com" aria-label="Client's email"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <Button type="submit" disabled={!email.trim()}>Invite client</Button>
        </form>
      )}
      {sent && <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300" role="status">{sent}</p>}
    </section>
  );
}

// A client asking for something: a title and the details. It lands in the backlog for the team, marked as theirs.
function RequestDialog({ project, onClose, onCreated }: { project: Project; onClose: () => void; onCreated: (taskId: string) => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    setBusy(true); setError(null);
    try {
      const r = await api<{ task: Task }>(`/orgs/${org.slug}/projects/${project.id}/tasks`, {
        method: "POST", body: { title: String(d.get("title") ?? "").trim(), description: String(d.get("description") ?? "").trim() },
      });
      await qc.invalidateQueries({ queryKey: projectKeys.tasks(org.slug, project.id) });
      onCreated(r.task.id);
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <Dialog open onClose={onClose} title="Ask for something">
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <p className="text-sm text-slate-500">The team at {org.name} sees it straight away and takes it from there.</p>
        <Field label="What do you need?" name="title" required maxLength={300} autoFocus placeholder="Add our opening hours to the contact page" />
        <div>
          <label htmlFor="request-details" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Details (optional)</label>
          <textarea id="request-details" name="description" rows={4} maxLength={20_000} className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>Send request</Button>
        </div>
      </form>
    </Dialog>
  );
}
